// src/pages/ConvertPage.jsx
// نسخه 2.0 — حرفه‌ای، هوشمند، با پیشرفت واقعی، Retry، Timeout و لغو
// ============================================================================
import { Link } from 'react-router-dom';
import React, { useState, useCallback, useRef, useEffect, useMemo } from 'react';
import './ConvertPage.css';

// پروتکل فعلی صفحه را دنبال می‌کند (http: یا https:)
const API_BASE_URL = '';

// ============================================================================
// ثابت‌های پیکربندی
// ============================================================================

const UPLOAD_TIMEOUT_MS = 120_000;       // ۲ دقیقه برای آپلود
const CONVERSION_TIMEOUT_MS = 600_000;   // ۱۰ دقیقه برای تبدیل + دانلود
const MAX_RETRIES = 2;                    // حداکثر ۲ بار تلاش مجدد
const RETRY_DELAY_MS = 1500;             // تأخیر پایه بین تلاش‌ها

// ============================================================================
// کامپوننت اصلی
// ============================================================================

const ConvertPage = () => {
  // ─── State های اصلی ───
  const [conversionType, setConversionType] = useState('word-to-pdf');
  const [file, setFile] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const [status, setStatus] = useState('idle'); // idle | uploading | converting | downloading | success | error
  const [progress, setProgress] = useState(0);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [statusMessage, setStatusMessage] = useState('');
  const [isConverting, setIsConverting] = useState(false);

  // ─── State های جدید (هوشمند) ───
  const [elapsedSeconds, setElapsedSeconds] = useState(0);
  const [requestId, setRequestId] = useState(null);
  const [cacheStatus, setCacheStatus] = useState(null); // 'HIT' | 'MISS' | null
  const [retryCount, setRetryCount] = useState(0);
  const [outputSize, setOutputSize] = useState(null);
  const [isOnline, setIsOnline] = useState(
    typeof navigator !== 'undefined' ? navigator.onLine : true
  );

  // ─── Refs ───
  const fileInputRef = useRef(null);
  const xhrRef = useRef(null);
  const timeoutRef = useRef(null);
  const timerRef = useRef(null);
  const abortRef = useRef(false);

  // ============================================================================
  // پیکربندی انواع تبدیل
  // ============================================================================
  const config = useMemo(() => ({
    'word-to-pdf': {
      label: 'Word → PDF',
      accept: '.docx,.doc',
      buttonText: 'تبدیل به PDF',
      apiEndpoint: `/word2pdf/api/convert/word-to-pdf`,
      outputExtension: 'pdf',
      outputMime: 'application/pdf',
      icon: '📄',
      maxSizeMB: 50,
      stageMessages: {
        uploading: 'در حال آپلود فایل به سرور...',
        converting: 'در حال تبدیل',
        downloading: 'در حال دریافت فایل PDF...',
      },
    },
    'pdf-to-word': {
      label: 'PDF → Word',
      accept: '.pdf',
      buttonText: 'تبدیل به Word',
      apiEndpoint: `/pdf2word/api/convert/pdf-to-word`,
      outputExtension: 'docx',
      outputMime: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      icon: '📝',
      maxSizeMB: 50,
      stageMessages: {
        uploading: 'در حال آپلود فایل به سرور...',
        converting: 'در حال تبدیل PDF به Word...',
        downloading: 'در حال دریافت فایل Word...',
      },
    },
  }), []);

  const currentConfig = config[conversionType];

  // ============================================================================
  // افکت‌های جانبی
  // ============================================================================

  // آزادسازی URL هنگام unmount یا تغییر نتیجه
  useEffect(() => {
    return () => {
      if (result?.url) URL.revokeObjectURL(result.url);
    };
  }, [result]);

  // نمایش آنلاین/آفلاین بودن
  useEffect(() => {
    const onOnline = () => setIsOnline(true);
    const onOffline = () => setIsOnline(false);
    window.addEventListener('online', onOnline);
    window.addEventListener('offline', onOffline);
    return () => {
      window.removeEventListener('online', onOnline);
      window.removeEventListener('offline', onOffline);
    };
  }, []);

  // تایمر زمان سپری‌شده
  useEffect(() => {
    if (isConverting) {
      setElapsedSeconds(0);
      timerRef.current = setInterval(() => {
        setElapsedSeconds((s) => s + 1);
      }, 1000);
    } else {
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    }
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [isConverting]);

  // پاکسازی هنگام unmount
  useEffect(() => {
    return () => {
      if (xhrRef.current) {
        try { xhrRef.current.abort(); } catch {}
      }
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, []);

  // ============================================================================
  // توابع کمکی
  // ============================================================================

  const formatBytes = (bytes) => {
    if (!bytes || bytes <= 0) return '0 B';
    const units = ['B', 'KB', 'MB', 'GB'];
    const i = Math.min(Math.floor(Math.log(bytes) / Math.log(1024)), units.length - 1);
    return `${(bytes / Math.pow(1024, i)).toFixed(i === 0 ? 0 : 2)} ${units[i]}`;
  };

  const formatTime = (seconds) => {
    if (seconds < 60) return `${seconds} ثانیه`;
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return `${m} دقیقه و ${s} ثانیه`;
  };

  const clearTimers = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  };

  const abortCurrentRequest = () => {
    abortRef.current = true;
    if (xhrRef.current) {
      try { xhrRef.current.abort(); } catch {}
      xhrRef.current = null;
    }
    clearTimers();
  };

  // ============================================================================
  // بازنشانی وضعیت
  // ============================================================================

  const resetState = useCallback(() => {
    abortCurrentRequest();
    abortRef.current = false;

    setFile(null);
    setResult(null);
    setError(null);
    setProgress(0);
    setStatus('idle');
    setStatusMessage('');
    setIsConverting(false);
    setElapsedSeconds(0);
    setRequestId(null);
    setCacheStatus(null);
    setRetryCount(0);
    setOutputSize(null);

    if (fileInputRef.current) fileInputRef.current.value = '';
  }, []);

  // ============================================================================
  // اعتبارسنجی فایل
  // ============================================================================

  const validateFile = useCallback((f) => {
    if (!f) return false;

    const maxSize = currentConfig.maxSizeMB * 1024 * 1024;
    const allowedExts = currentConfig.accept.split(',').map((e) => e.trim().toLowerCase());
    const fileExt = '.' + f.name.split('.').pop().toLowerCase();

    if (!allowedExts.includes(fileExt)) {
      setError(`فرمت فایل پشتیبانی نمی‌شود. فرمت‌های مجاز: ${currentConfig.accept}`);
      setStatus('error');
      return false;
    }
    if (f.size > maxSize) {
      setError(
        `حجم فایل (${formatBytes(f.size)}) بیش از حد مجاز است. حداکثر: ${currentConfig.maxSizeMB} مگابایت`
      );
      setStatus('error');
      return false;
    }
    if (f.size === 0) {
      setError('فایل انتخاب‌شده خالی است');
      setStatus('error');
      return false;
    }
    return true;
  }, [currentConfig]);

  // ============================================================================
  // Drag & Drop Handlers
  // ============================================================================

  const handleDragEnter = useCallback((e) => {
    e.preventDefault(); e.stopPropagation();
    if (e.dataTransfer?.types?.includes('Files')) setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e) => {
    e.preventDefault(); e.stopPropagation();
    // فقط زمانی که از خود drop zone خارج شویم
    if (e.currentTarget === e.target) setIsDragging(false);
  }, []);

  const handleDragOver = useCallback((e) => {
    e.preventDefault(); e.stopPropagation();
    if (e.dataTransfer) e.dataTransfer.dropEffect = 'copy';
  }, []);

  const applySelectedFile = useCallback((selectedFile) => {
    if (!validateFile(selectedFile)) return;
    setFile(selectedFile);
    setError(null);
    setResult(null);
    setStatus('idle');
    setStatusMessage(`فایل "${selectedFile.name}" آماده تبدیل است.`);
    setRequestId(null);
    setCacheStatus(null);
    setOutputSize(null);
    setRetryCount(0);
    setElapsedSeconds(0);
  }, [validateFile]);

  const handleDrop = useCallback((e) => {
    e.preventDefault(); e.stopPropagation();
    setIsDragging(false);

    const droppedFiles = e.dataTransfer?.files;
    if (!droppedFiles?.length) return;

    // فقط اولین فایل را می‌گیریم (رفتار ساده و قابل پیش‌بینی)
    const selectedFile = droppedFiles[0];

    if (droppedFiles.length > 1) {
      setStatusMessage(
        `توجه: فقط اولین فایل ("${selectedFile.name}") انتخاب شد. برای تبدیل چندگانه، هر فایل را جداگانه تبدیل کنید.`
      );
    }

    applySelectedFile(selectedFile);
  }, [applySelectedFile]);

  const handleFileSelect = useCallback((e) => {
    const selectedFile = e.target.files?.[0];
    if (selectedFile) applySelectedFile(selectedFile);
  }, [applySelectedFile]);

  // ============================================================================
  // هسته تبدیل — با XMLHttpRequest برای پیشرفت واقعی آپلود
  // ============================================================================

  /**
   * یک تلاش واحد برای تبدیل
   * @returns {Promise<{blob, fileName, cacheStatus, requestId}>}
   */
  const performConversion = useCallback((attemptNumber = 0) => {
    return new Promise((resolve, reject) => {
      if (abortRef.current) {
        return reject(Object.assign(new Error('aborted'), { name: 'AbortError' }));
      }

      const formData = new FormData();
      formData.append('file', file);

      const xhr = new XMLHttpRequest();
      xhrRef.current = xhr;
      xhr.responseType = 'blob';

      // ─── Timeout کل ───
      timeoutRef.current = setTimeout(() => {
        try { xhr.abort(); } catch {}
        const err = new Error(
          `زمان تبدیل بیش از حد طول کشید (بیش از ${CONVERSION_TIMEOUT_MS / 1000} ثانیه). لطفاً دوباره تلاش کنید.`
        );
        err.name = 'TimeoutError';
        reject(err);
      }, CONVERSION_TIMEOUT_MS);

      // ─── پیشرفت آپلود ───
      xhr.upload.onprogress = (event) => {
        if (event.lengthComputable) {
          const uploadPercent = Math.round((event.loaded / event.total) * 100);
          // آپلود ۰-۳۰٪ از نوار پیشرفت را اشغال می‌کند
          setProgress(Math.min(Math.round(uploadPercent * 0.3), 30));
          if (uploadPercent < 100) {
            setStatus('uploading');
            setStatusMessage(
              `${currentConfig.stageMessages.uploading} (${formatBytes(event.loaded)} از ${formatBytes(event.total)})`
            );
          } else {
            setStatus('converting');
            setStatusMessage(currentConfig.stageMessages.converting);
            setProgress(35);
          }
        }
      };

      // ─── پایان آپلود ───
      xhr.upload.onload = () => {
        setStatus('converting');
        setStatusMessage(currentConfig.stageMessages.converting);
        setProgress(35);
      };

      // ─── دریافت پاسخ ───
      xhr.onload = () => {
        clearTimers();

        // استخراج هدرها
        const reqId = xhr.getResponseHeader('X-Request-Id') || null;
        const cacheHdr = xhr.getResponseHeader('X-Cache') || null;

        // ─── موفقیت ───
        if (xhr.status >= 200 && xhr.status < 300) {
          const blob = xhr.response;
          const fileName =
            file.name.replace(/\.[^/.]+$/, '') + '.' + currentConfig.outputExtension;

          // استخراج نام فایل از هدر (اگر وجود دارد)
          let serverFileName = fileName;
          const disposition = xhr.getResponseHeader('Content-Disposition');
          if (disposition) {
            const match = /filename\*?=(?:UTF-8''|")?([^";]+)/i.exec(disposition);
            if (match) {
              try { serverFileName = decodeURIComponent(match[1].replace(/"/g, '')); }
              catch { serverFileName = match[1].replace(/"/g, ''); }
            }
          }

          setProgress(100);
          return resolve({
            blob,
            fileName: serverFileName,
            cacheStatus: cacheHdr,
            requestId: reqId,
            size: blob.size,
          });
        }

        // ─── خطاهای HTTP ───
        const handleHttpError = async () => {
          let errorMessage = `خطا در تبدیل (کد ${xhr.status})`;
          let serverRequestId = reqId;

          try {
            // پاسخ به صورت Blob است، پس باید به text تبدیل شود
            const text = await xhr.response.text();
            const data = JSON.parse(text);
            errorMessage = data.error || errorMessage;
            serverRequestId = data.requestId || serverRequestId;
          } catch {
            // پاسخ JSON نبود
          }

          const err = new Error(errorMessage);
          err.name = 'HttpError';
          err.status = xhr.status;
          err.requestId = serverRequestId;

          // پیام‌های خاص برای کدهای خاص
          if (xhr.status === 429) {
            err.message = 'تعداد درخواست‌های شما بیش از حد مجاز است. لطفاً چند دقیقه صبر کنید.';
            err.isRateLimit = true;
          } else if (xhr.status === 413) {
            err.message = `حجم فایل بیش از حد مجاز است (حداکثر ${currentConfig.maxSizeMB} مگابایت)`;
            err.isPayloadTooLarge = true;
          } else if (xhr.status === 415) {
            err.message = 'فرمت فایل پشتیبانی نمی‌شود';
            err.isUnsupportedMedia = true;
          } else if (xhr.status >= 500) {
            err.isServerError = true;
          }

          reject(err);
        };

        handleHttpError();
      };

      // ─── خطای شبکه ───
      xhr.onerror = () => {
        clearTimers();
        const err = new Error(
          isOnline
            ? 'خطا در ارتباط با سرور. لطفاً اتصال اینترنت خود را بررسی کنید.'
            : 'اتصال اینترنت قطع است. لطفاً اتصال خود را بررسی کنید.'
        );
        err.name = 'NetworkError';
        err.isNetworkError = true;
        reject(err);
      };

      // ─── لغو ───
      xhr.onabort = () => {
        clearTimers();
        const err = new Error('aborted');
        err.name = 'AbortError';
        reject(err);
      };

      // ─── Timeout خود XHR (اضافی برای اطمینان) ───
      xhr.ontimeout = () => {
        clearTimers();
        const err = new Error('زمان درخواست به پایان رسید');
        err.name = 'TimeoutError';
        reject(err);
      };

      // ─── ارسال ───
      try {
        xhr.open('POST', currentConfig.apiEndpoint, true);
        xhr.send(formData);
      } catch (err) {
        clearTimers();
        reject(new Error('خطا در ارسال درخواست: ' + err.message));
      }
    });
  }, [file, currentConfig, isOnline]);

  // ============================================================================
  // منطق Retry هوشمند
  // ============================================================================

  const shouldRetry = useCallback((err) => {
    // هرگز روی این خطاها Retry نکن
    if (err.name === 'AbortError') return false;
    if (err.isRateLimit) return false;
    if (err.isPayloadTooLarge) return false;
    if (err.isUnsupportedMedia) return false;
    if (err.status && err.status >= 400 && err.status < 500) return false;

    // فقط روی خطاهای گذرا Retry کن
    return (
      err.isNetworkError ||
      err.isServerError ||
      err.name === 'TimeoutError' ||
      err.name === 'NetworkError'
    );
  }, []);

  const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

  // ============================================================================
  // هندلر اصلی تبدیل
  // ============================================================================

  const handleConvert = useCallback(async () => {
    if (!file) {
      setError('لطفاً ابتدا یک فایل انتخاب کنید.');
      setStatus('error');
      return;
    }

    if (!isOnline) {
      setError('اتصال اینترنت قطع است. لطفاً اتصال خود را بررسی کنید.');
      setStatus('error');
      return;
    }

    // ─── مقداردهی اولیه ───
    abortRef.current = false;
    setStatus('uploading');
    setIsConverting(true);
    setError(null);
    setResult(null);
    setProgress(0);
    setStatusMessage(currentConfig.stageMessages.uploading);
    setElapsedSeconds(0);
    setRequestId(null);
    setCacheStatus(null);
    setRetryCount(0);
    setOutputSize(null);

    let lastError = null;

    // ─── حلقه تلاش ───
    for (let attempt = 0; attempt <= MAX_RETRIES; attempt++) {
      if (abortRef.current) {
        setStatus('idle');
        setStatusMessage('⏹️ عملیات لغو شد.');
        setIsConverting(false);
        return;
      }

      if (attempt > 0) {
        const delay = RETRY_DELAY_MS * attempt; // تأخیر نمایی ساده
        setRetryCount(attempt);
        setStatusMessage(
          `تلاش مجدد (${attempt} از ${MAX_RETRIES}) — انتظار ${delay / 1000} ثانیه...`
        );
        setProgress(0);
        await sleep(delay);
        if (abortRef.current) continue;

        // بازنشانی نوار پیشرفت قبل از تلاش جدید
        setProgress(0);
        setStatus('uploading');
        setStatusMessage(currentConfig.stageMessages.uploading);
      }

      try {
        const output = await performConversion(attempt);

        // ─── موفقیت ───
        const url = URL.createObjectURL(output.blob);

        setResult({
          blob: output.blob,
          fileName: output.fileName,
          url,
          size: output.size,
        });

        setRequestId(output.requestId);
        setCacheStatus(output.cacheStatus);
        setOutputSize(output.size);
        setProgress(100);
        setStatus('success');
        setStatusMessage(
          output.cacheStatus === 'HIT'
            ? '✅ تبدیل از کش بازیابی شد (سریع‌تر از حد انتظار)'
            : '✅ تبدیل با موفقیت انجام شد!'
        );
        setIsConverting(false);
        return;
      } catch (err) {
        lastError = err;

        if (err.name === 'AbortError') {
          setStatus('idle');
          setStatusMessage('⏹️ عملیات لغو شد.');
          setIsConverting(false);
          return;
        }

        // اگر Retry مجاز نیست، بلافاصله خطا را نشان بده
        if (!shouldRetry(err) || attempt >= MAX_RETRIES) {
          break;
        }

        // در غیر این صورت، به تلاش بعدی برو
      }
    }

    // ─── شکست نهایی ───
    console.error('خطا در تبدیل:', lastError);

    if (lastError) {
      setError(lastError.message || 'خطا در تبدیل فایل');
      if (lastError.requestId) setRequestId(lastError.requestId);
      setStatus('error');
      setStatusMessage(
        retryCount > 0
          ? `❌ تبدیل پس از ${retryCount + 1} تلاش ناموفق بود`
          : '❌ خطا در تبدیل'
      );
    }

    setIsConverting(false);
    setProgress(0);
  }, [file, isOnline, currentConfig, performConversion, shouldRetry, retryCount]);

  // ============================================================================
  // لغو تبدیل
  // ============================================================================

  const handleCancel = useCallback(() => {
    abortCurrentRequest();
    setStatus('idle');
    setStatusMessage('⏹️ عملیات لغو شد.');
    setIsConverting(false);
    setProgress(0);
  }, []);

  // ============================================================================
  // دانلود نتیجه
  // ============================================================================

  const handleDownload = useCallback(() => {
    if (!result) return;
    const link = document.createElement('a');
    link.href = result.url;
    link.download = result.fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }, [result]);

  // ============================================================================
  // دانلود با نام دلخواه (قابلیت حرفه‌ای)
  // ============================================================================

  const handleDownloadAs = useCallback(() => {
    if (!result) return;
    const newName = window.prompt('نام فایل را وارد کنید:', result.fileName);
    if (!newName) return;
    const link = document.createElement('a');
    link.href = result.url;
    link.download = newName.endsWith('.' + currentConfig.outputExtension)
      ? newName
      : `${newName}.${currentConfig.outputExtension}`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }, [result, currentConfig]);

  // ============================================================================
  // تعویض نوع تبدیل
  // ============================================================================

  const handleTypeChange = useCallback((type) => {
    if (isConverting) return;
    setConversionType(type);
    resetState();
  }, [isConverting, resetState]);

  // ============================================================================
  // رندر
  // ============================================================================

  return (
    <div className="convert-page">
      <div className="convert-container">



  {/* ─── دکمه بازگشت ─── */}
  <Link to="/" className="back-link" aria-label="بازگشت به صفحه اصلی" title="بازگشت">
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <line x1="19" y1="12" x2="5" y2="12" />
      <polyline points="12 19 5 12 12 5" />
    </svg>
  </Link>




        {/* ─── هدر ─── */}
        <div className="convert-header">
          <h1>🔁 مبدل حرفه‌ای Word ↔ PDF</h1>
          <p>تبدیل واقعی با کیفیت بالا — حفظ کامل قالب‌بندی، فونت‌ها و متون راست‌به‌چپ</p>
        </div>

        {/* ─── نمایش آفلاین ─── */}
        {!isOnline && (
          <div className="error-message" style={{ marginBottom: '1rem' }}>
            <span>📡</span> اتصال اینترنت قطع است. لطفاً اتصال خود را بررسی کنید.
          </div>
        )}

        {/* ─── انتخاب نوع تبدیل ─── */}
        <div className="conversion-selector">
          <button
            className={`selector-btn ${conversionType === 'word-to-pdf' ? 'active' : ''}`}
            onClick={() => handleTypeChange('word-to-pdf')}
            disabled={isConverting}
            type="button"
          >
            <span className="btn-icon">📄</span>
            Word → PDF
          </button>
          {/*<button
            className={`selector-btn ${conversionType === 'pdf-to-word' ? 'active' : ''}`}
            onClick={() => handleTypeChange('pdf-to-word')}
            disabled={isConverting}
            type="button"
          >
            <span className="btn-icon">📝</span>
            PDF → Word
          </button>*/}
        </div>

        {/* ─── ناحیه Drag & Drop ─── */}
        <div
          className={`drop-zone ${isDragging ? 'dragging' : ''} ${file ? 'has-file' : ''}`}
          onDragEnter={handleDragEnter}
          onDragLeave={handleDragLeave}
          onDragOver={handleDragOver}
          onDrop={handleDrop}
          onClick={() => !isConverting && fileInputRef.current?.click()}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => {
            if ((e.key === 'Enter' || e.key === ' ') && !isConverting) {
              e.preventDefault();
              fileInputRef.current?.click();
            }
          }}
        >
          <input
            ref={fileInputRef}
            type="file"
            accept={currentConfig.accept}
            onChange={handleFileSelect}
            style={{ display: 'none' }}
            disabled={isConverting}
          />

          {!file ? (
            <div className="drop-content">
              <div className="drop-icon">📤</div>
              <h3>فایل خود را اینجا بکشید و رها کنید</h3>
              <p>یا برای انتخاب فایل کلیک کنید</p>
              <span className="drop-hint">
                فایل‌های {currentConfig.accept} پشتیبانی می‌شوند (حداکثر {currentConfig.maxSizeMB}MB)
              </span>
            </div>
          ) : (
            <div className="file-info">
              <div className="file-icon">📎</div>
              <div className="file-details">
                <div className="file-name">{file.name}</div>
                <div className="file-size">
                  حجم ورودی: {formatBytes(file.size)}
                </div>
                <div className="file-type">نوع تبدیل: {currentConfig.label}</div>
              </div>
              <button
                className="remove-file-btn"
                onClick={(e) => { e.stopPropagation(); resetState(); }}
                disabled={isConverting}
                aria-label="حذف فایل"
                type="button"
              >
                ✕
              </button>
            </div>
          )}
        </div>

        {/* ─── دکمه‌های عمل ─── */}
        <div className="convert-actions">
          <button
            className="convert-btn"
            onClick={handleConvert}
            disabled={!file || isConverting || !isOnline}
            type="button"
          >
            {isConverting ? (
              <>
                <span className="spinner"></span>
                در حال تبدیل...
              </>
            ) : (
              currentConfig.buttonText
            )}
          </button>

          {isConverting && (
            <button
              className="reset-btn"
              onClick={handleCancel}
              type="button"
              style={{ marginRight: '0.5rem' }}
            >
              ⏹️ لغو
            </button>
          )}

          {isConverting && (
            <div className="progress-container">
              <div className="progress-bar" style={{ width: `${progress}%` }}></div>
              <span className="progress-text">{progress}%</span>
            </div>
          )}
        </div>

        {/* ─── پیام وضعیت ─── */}
        {statusMessage && (
          <div
            className={`status-message ${
              status === 'error' ? 'error' : status === 'success' ? 'success' : 'info'
            }`}
          >
            {statusMessage}
          </div>
        )}

        {/* ─── نمایش اطلاعات پیشرفت ─── */}
        {isConverting && (
          <div className="status-message info" style={{ fontSize: '0.9rem' }}>
            ⏱️ زمان سپری‌شده: {formatTime(elapsedSeconds)}
            {retryCount > 0 && ` — تلاش ${retryCount + 1} از ${MAX_RETRIES + 1}`}
            {cacheStatus === 'HIT' && ' — ⚡ کش'}
          </div>
        )}

        {/* ─── خطا ─── */}
        {error && (
          <div className="error-message">
            <span>⚠️</span> {error}
            {requestId && (
              <div style={{ fontSize: '0.8rem', opacity: 0.7, marginTop: '0.5rem' }}>
                کد پیگیری: <code>{requestId}</code>
              </div>
            )}
            {(status === 'error' || error) && file && (
              <button
                className="reset-btn"
                onClick={handleConvert}
                type="button"
                style={{ marginTop: '0.5rem' }}
              >
                🔄 تلاش مجدد
              </button>
            )}
          </div>
        )}

        {/* ─── نتیجه ─── */}
        {status === 'success' && result && (
          <div className="result-container">
            <div className="result-icon">✅</div>
            <h3>تبدیل با موفقیت انجام شد!</h3>
            <p>فایل خروجی: <strong>{result.fileName}</strong></p>
            {outputSize && (
              <p style={{ fontSize: '0.9rem', opacity: 0.8 }}>
                حجم خروجی: {formatBytes(outputSize)}
              </p>
            )}
            {cacheStatus === 'HIT' && (
              <p style={{ fontSize: '0.85rem', color: '#16a34a' }}>
                ⚡ این نتیجه از کش سرور بازیابی شد
              </p>
            )}
            <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'center', flexWrap: 'wrap' }}>
              <button className="download-btn" onClick={handleDownload} type="button">
                ⬇️ دانلود فایل {currentConfig.outputExtension.toUpperCase()}
              </button>
              <button className="reset-btn" onClick={handleDownloadAs} type="button">
                💾 ذخیره با نام دلخواه
              </button>
            </div>
            <button className="reset-btn" onClick={resetState} type="button" style={{ marginTop: '0.5rem' }}>
              تبدیل فایل دیگر
            </button>
          </div>
        )}

        {/* ─── ویژگی‌ها ─── */}
        <div className="features-grid">
          <div className="feature-item">
            <span className="feature-icon">🎯</span>
            <div>
              <h4>دقت بالا</h4>
              <p>حفظ کامل قالب‌بندی، جداول و متون فارسی</p>
            </div>
          </div>
          <div className="feature-item">
            <span className="feature-icon">⚡</span>
            <div>
              <h4>سرعت فوق‌العاده</h4>
              <p>کش هوشمند و تبدیل موازی</p>
            </div>
          </div>
          <div className="feature-item">
            <span className="feature-icon">🔒</span>
            <div>
              <h4>امنیت کامل</h4>
              <p>فایل‌ها پس از تبدیل حذف می‌شوند</p>
            </div>
          </div>
          <div className="feature-item">
            <span className="feature-icon">📱</span>
            <div>
              <h4>واکنش‌گرا</h4>
              <p>سازگار با تمام دستگاه‌ها</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ConvertPage;